import { Link } from "@tanstack/react-router";
import { Alert, Card, Col, Empty, Row, Skeleton, Statistic, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useAuditList } from "@/modules/audit/hooks/useAudit";
import { amalNomi } from "@/modules/audit/utils/actions";
import { XAVF_RANGI } from "@/modules/dashboard/components/StatCard";
import { davomatDarajasi, foizMatn } from "@/modules/dashboard/utils/davomatDarajasi";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useDepartmentSummary, useDepartmentsExplorer } from "../hooks/useDepartments";

const { Text } = Typography;

/** Faqat BIRINCHI shuncha faol farzand ko'rsatiladi — qolgani "Barchasi" havolasida. */
const CHILDREN_PREVIEW_LIMIT = 5;
/** Faqat OXIRGI shuncha audit yozuvi — backend `desc(at)` bilan qaytaradi. */
const RECENT_CHANGES_LIMIT = 5;

/**
 * «Umumiy» yorlig'i — `GET /departments/{id}/summary` agregat kartalari.
 *
 * ⚠️⚠️ **Foiz FAQAT `davomatDarajasi()` + `foizMatn()` orqali** — dashboard
 * bilan bitta manba (spec §6.6: formula backend'ga ko'chirilmaydi va
 * ikkilanmaydi). `davomatDarajasi` YAXLITLANMAGAN son qaytaradi (2/3 →
 * 66.666…), `Statistic` esa `precision` siz uni AYNAN chizardi; `foizMatn`
 * dashboard'dagi kabi vergulli bir kasr beradi va `null` ni «—» qiladi.
 * `null` ikki holatda: `todayAttendance = null` (pastki daraxtda xodim
 * yo'q) va `plannedShifts = 0` (bugun smena rejalanmagan) — ikkalasi ham
 * «0%» EMAS.
 *
 * ⚠️ `muddatiOtgan` alohida status emas — u `faol` ICHIDA ham sanaladi
 * (`DepartmentSummary` izohi), ya'ni ikki karta yig'indisi ma'nosiz.
 */
export function DepartmentOverviewTab({ departmentId }: { departmentId: string }) {
	const { t } = useTranslation("departments");
	const { data: summary, isLoading, isError } = useDepartmentSummary(departmentId);

	if (isError) {
		return <Alert type="error" showIcon title={t("overview.loadError")} />;
	}

	if (isLoading || !summary) {
		return <Skeleton active />;
	}

	const rate = summary.todayAttendance
		? davomatDarajasi(summary.todayAttendance.boshlangan, summary.todayAttendance.plannedShifts)
		: null;
	const muddatiOtgan = summary.tasksByStatus.muddatiOtgan;

	return (
		<>
			<Row gutter={[16, 16]}>
				<Col xs={24} sm={8}>
					<Card>
						<Statistic title={t("overview.childrenCount")} value={summary.childrenCount} />
					</Card>
				</Col>
				<Col xs={24} sm={8}>
					<Card>
						<Statistic
							title={t("overview.employeesCount")}
							value={summary.employeesCountWithDescendants}
							suffix={`(${t("overview.direct")}: ${summary.employeesCountDirect})`}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={8}>
					<Card>
						<Statistic title={t("overview.todayAttendance")} value={foizMatn(rate)} />
					</Card>
				</Col>
				<Col xs={24} sm={6}>
					<Card>
						<Statistic title={t("overview.tasksActive")} value={summary.tasksByStatus.faol} />
					</Card>
				</Col>
				<Col xs={24} sm={6}>
					<Card>
						{/* ⚠️ `valueStyle` antd 6 da eskirgan — `StatCard` bilan bir xil
					    `styles.content`. Qizil FAQAT noldan katta bo'lganda: doimo
					    qizil karta «hammasi yomon» degan yolg'on taassurot berardi. */}
						<Statistic
							title={t("overview.tasksOverdue")}
							value={muddatiOtgan}
							styles={muddatiOtgan > 0 ? { content: { color: XAVF_RANGI } } : undefined}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={6}>
					<Card>
						<Statistic title={t("overview.tasksDone")} value={summary.tasksByStatus.yakunlangan} />
					</Card>
				</Col>
				<Col xs={24} sm={6}>
					<Card>
						<Statistic title={t("overview.zonesCount")} value={summary.zonesCountDirect} />
					</Card>
				</Col>
			</Row>

			<Row gutter={[16, 16]} className="mt-4">
				<Col xs={24} md={12}>
					<SubunitsPreview departmentId={departmentId} />
				</Col>
				<Col xs={24} md={12}>
					{/* ⚠️ `RecentChanges` o'zi `useAuditList` ni chaqiradi — shu bilan
				    ruxsatsiz foydalanuvchida SO'ROV UMUMAN yubormaydi (komponent
				    `PermissionGate` ostida hech qachon mount bo'lmaydi). */}
					<PermissionGate resource="audit" action="read">
						<RecentChanges departmentId={departmentId} />
					</PermissionGate>
				</Col>
			</Row>
		</>
	);
}

/**
 * "Bo'linmalar" mini-ro'yxati — birinchi {@link CHILDREN_PREVIEW_LIMIT} ta
 * FAOL farzand bo'lim (spec §8, C2 Task 1a). Har biri o'z profiliga
 * tiplangan `Link` bilan bog'lanadi; ostida "Barchasi" havolasi joriy
 * bo'limning `?tab=bolinmalar`iga o'tadi (`DepartmentSubunitsTab`ning
 * xuddi shu bo'lim uchun to'liq ro'yxati).
 *
 * ⚠️ Ruxsat darvozasi YO'Q — `DEPARTMENT_TAB_PERMISSION`da "bolinmalar"
 * tabi ro'yxatda yo'q, ya'ni har doim ruxsat etilgan (`isTabAllowed`).
 */
function SubunitsPreview({ departmentId }: { departmentId: string }) {
	const { t } = useTranslation("departments");
	const query = useDepartmentsExplorer({ parentId: departmentId, isActive: true });
	const children = query.items.slice(0, CHILDREN_PREVIEW_LIMIT);

	return (
		<Card title={t("overview.subunitsTitle")}>
			{query.isLoading ? (
				<Skeleton active paragraph={{ rows: 3 }} title={false} />
			) : children.length === 0 ? (
				<Text type="secondary">{t("overview.subunitsEmpty")}</Text>
			) : (
				<>
					<ul className="m-0 flex list-none flex-col gap-1 p-0">
						{children.map((item) => (
							<li key={item.id}>
								<Link
									to="/departments/$id"
									params={{ id: item.id }}
									className="text-[#1c5cab] hover:underline"
								>
									{item.name}
								</Link>
							</li>
						))}
					</ul>
					<Link
						to="/departments/$id"
						params={{ id: departmentId }}
						search={{ tab: "bolinmalar" }}
						className="mt-2 inline-block text-[#1c5cab] hover:underline"
					>
						{t("overview.subunitsViewAll")}
					</Link>
				</>
			)}
		</Card>
	);
}

/**
 * "So'nggi o'zgarishlar" — `resource=departments&resourceId=<bo'lim id>`
 * bo'yicha oxirgi {@link RECENT_CHANGES_LIMIT} ta audit yozuvi: amal, kim
 * va qachon (spec §8, C2 Task 1b). Yorliq va format helper'lari audit
 * modulidan (`amalNomi`) — ikkilanmaydi.
 *
 * ⚠️ FAQAT `PermissionGate audit:read` ostida mount qilinadi (yuqorida) —
 * shu komponentning o'zi ruxsatni QAYTA tekshirmaydi.
 */
function RecentChanges({ departmentId }: { departmentId: string }) {
	const { t } = useTranslation("departments");
	const { t: tAudit } = useTranslation("audit");
	const query = useAuditList({ resourceType: "departments", resourceId: departmentId });
	const entries = query.items.slice(0, RECENT_CHANGES_LIMIT);

	return (
		<Card title={t("overview.recentChangesTitle")}>
			{query.isLoading ? (
				<Skeleton active paragraph={{ rows: 3 }} title={false} />
			) : entries.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={t("overview.recentChangesEmpty")}
				/>
			) : (
				<ul className="m-0 flex list-none flex-col gap-1 p-0">
					{entries.map((entry) => (
						<li key={entry.id} data-audit-entry className="text-xs">
							<Text>{amalNomi(entry.action, tAudit)}</Text>{" "}
							<Text type="secondary">
								— {entry.actorName ?? tAudit("systemActor")} ·{" "}
								{dayjs(entry.at).format("DD.MM.YYYY HH:mm")}
							</Text>
						</li>
					))}
				</ul>
			)}
		</Card>
	);
}
